/**
 * IDLEMILE - Centralized Configuration Constants
 *
 * This file contains all "magic numbers" and configuration values used throughout
 * the game. Centralizing these makes it easier to:
 * - Find and adjust values
 * - Understand what each value controls
 * - Maintain consistency across the codebase
 *
 * Organization:
 * - CAMERA: Camera positioning and movement
 * - RENDERING: Resolution, fog, visual settings
 * - LIGHTING: Ambient, directional, headlights
 * - SKY: Sky sphere settings
 * - STARS: Star field configuration
 * - SUN: Sun appearance and arc
 * - CLOUDS: Cloud generation and behavior
 * - COLOR_CYCLE: Day/night timing
 * - CAR: Car positioning and animation
 * - EQUIPMENT: Item slot positions
 * - BIRDS: Bird flock settings
 * - PLANES: Airplane settings
 */

// =============================================================================
// CAMERA SETTINGS
// =============================================================================

export const CAMERA = {
    // Field of view (degrees) - higher = wider view, lower = more zoomed
    FOV: 75,

    // Near/far clipping planes
    NEAR_PLANE: 0.1,
    FAR_PLANE: 5000,

    // Camera height above the road surface
    HEIGHT: 1,

    // How far ahead the camera looks (affects curve visibility)
    LOOK_AHEAD_BASE: 40,

    // Vertical look target - higher = look toward horizon, lower = look at road
    LOOK_TARGET_HEIGHT: 0,

    // Left/right offset from road center (-4 = left, 4 = right, 0 = center)
    LATERAL_OFFSET: 2,

    // Smoothing factor for camera movement (0-1)
    // Higher = snappier following, Lower = smoother but may drift
    LERP_FACTOR: 0.5,

    // Initial camera position
    INITIAL_POSITION: { x: 0, y: 5, z: 0 },

    // Initial look-at target
    INITIAL_LOOK_AT: { x: 0, y: 3, z: -20 },

    // ─────────────────────────────────────────────────────────────────────────
    // ROAD FOLLOWING - Look-at targeting system
    // Camera looks at a point ahead on the spline, providing natural smoothing
    // ─────────────────────────────────────────────────────────────────────────

    // How far ahead on the road spline to look (units)
    // This is the key parameter - provides geometric smoothing on curves
    // Higher = smoother turns, more anticipation, may feel floaty
    // Lower = tighter/more responsive, may feel jerky on sharp curves
    // Recommended range: 30-80
    LOOK_AT_DISTANCE: 20,

    // Light smoothing to prevent micro-jitter (not for curve following)
    // The look-at geometry provides most of the smoothing
    // These are just for eliminating noise/jitter
    // Keep low (2-6) - higher values add lag
    YAW_SMOOTHING: 4.0,
    PITCH_SMOOTHING: 3.0,

    // Static yaw offset (radians) - slight rightward view to see road edge
    // Negative = look slightly right, Positive = look slightly left
    YAW_OFFSET: -0.09,

    // Maximum pitch angle (radians) - prevents extreme up/down angles
    // 0.35 radians ≈ 20 degrees
    MAX_PITCH: 0.35,
};

// =============================================================================
// RENDERING SETTINGS
// =============================================================================

export const RENDERING = {
    // Resolution scale relative to native display (0.5 = half resolution)
    // Lower = better performance, more pixelated look
    // 0.5 is a good balance of performance and visual quality
    RESOLUTION_SCALE: 0.35,

    // Shadow map resolution (power of 2) - higher = better quality
    SHADOW_MAP_SIZE: 512,

    // Shadow camera bounds - balance between quality and coverage
    SHADOW_CAMERA_SIZE: 400,
    SHADOW_CAMERA_NEAR: 1,
    SHADOW_CAMERA_FAR: 1500,

    // UI update throttle (milliseconds) - limits DOM updates for performance
    UI_UPDATE_INTERVAL: 250,
};

// =============================================================================
// FOG SETTINGS
// =============================================================================

export const FOG = {
    // Distance where fog starts (closer = more visible fog)
    NEAR: 700,
    NEAR_MOBILE: 1000,

    // Distance where fog is fully opaque (objects beyond are hidden)
    FAR: 3500,
    FAR_MOBILE: 2000,

    // Default fog color (overridden by palette)
    DEFAULT_COLOR: 0xa899c7,
};

// =============================================================================
// LIGHTING SETTINGS
// =============================================================================

export const LIGHTING = {
    // Ambient light defaults (overridden by time-of-day palette)
    AMBIENT: {
        DEFAULT_COLOR: 0xb19cd9,
        DEFAULT_INTENSITY: 0.4,
    },

    // Directional (sun) light defaults
    DIRECTIONAL: {
        DEFAULT_COLOR: 0xd8b9f7,
        DEFAULT_INTENSITY: 0.9,
        POSITION: { x: 50, y: 100, z: 50 },
    },

    // Headlights (night driving)
    HEADLIGHTS: {
        ENABLED: true,
        BRIGHTNESS: 500,           // Light intensity
        DISTANCE: 2000,            // How far light reaches
        ANGLE: Math.PI / 4,        // Cone angle (radians)
        PENUMBRA: 0.5,             // Soft edge falloff
        DECAY: 2,                  // Light decay rate
        AUTO_NIGHT_ONLY: true,     // Only show at night
        NIGHT_THRESHOLD: 0.149,     // Ambient intensity below this = night (lowered for shadow lighting)
        SPREAD: 1.2,               // Left/right distance from center
        OFFSET_Y: 0,               // Vertical offset from camera
        OFFSET_Z: -2,              // Forward offset from camera
        TARGET_OFFSET_Z: -50,      // How far ahead lights point
    },
};

// =============================================================================
// SKY SETTINGS
// =============================================================================

export const SKY = {
    // Sky sphere radius (should contain far plane and clouds)
    RADIUS: 5000,

    // Sphere detail level (higher = smoother gradient)
    SEGMENTS: 32,

    // Default colors (overridden by palette)
    DEFAULT_TOP_COLOR: 0x9d84b7,
    DEFAULT_BOTTOM_COLOR: 0xa899c7,
};

// =============================================================================
// STAR FIELD SETTINGS
// =============================================================================

export const STARS = {
    ENABLED: true,

    // Number of stars to generate
    COUNT: 700,

    // Star point size
    SIZE: 7.0,

    // Distance range from camera (must be within FAR_PLANE of 5000)
    // Stars should be further than sun (4800) so sun occludes them
    MIN_DISTANCE: 4850,
    MAX_DISTANCE: 4950,

    // Brightness/opacity
    BRIGHTNESS: 3,

    // Stars fade when ambient light exceeds this value
    // Night ambient is 0.10, dawn ~0.18, sunset ~0.15, day 0.20
    // Stars should only be visible at night, appearing near the end of sunset
    FADE_THRESHOLD: 0.08,
};

// =============================================================================
// SUN SETTINGS
// =============================================================================

export const SUN = {
    ENABLED: true,

    // Sun sphere size
    SIZE: 350,

    // Distance from camera (should be near far plane for proper depth)
    DISTANCE: 4800,

    // How high sun rises above horizon at peak
    ARC_HEIGHT: 2500,

    // Default color (overridden by palette)
    DEFAULT_COLOR: 0xfffef0,

    // Sun fades when ambient light drops below this
    // (lowered since we reduced ambient for shadow-based lighting)
    FADE_THRESHOLD: 0.12,

    // Sun arc positions (0.0 = behind, 0.5 = overhead, 1.0 = front)
    // These control where on the arc the sun appears during each phase
    ARC: {
        DAWN_START: 0.0,
        DAWN_END: 0.35,
        DAY_START: 0.35,
        DAY_END: 0.85,
        SUNSET_START: 0.85,
        SUNSET_END: 1.15,
    },
};

// =============================================================================
// CLOUD SETTINGS
// =============================================================================

export const CLOUDS = {
    ENABLED: true,

    // Number of cloud sprites to maintain
    COUNT: 120,
    COUNT_MOBILE: 50,

    // Height range above camera
    // Keep max height within sky sphere to prevent clipping
    MIN_HEIGHT: 600,
    MAX_HEIGHT: 2500,

    // Horizontal distance range
    // Must stay within sky sphere radius
    MIN_DISTANCE: 400,
    MAX_DISTANCE: 3000,
    MAX_DISTANCE_MOBILE: 1800,

    // Cloud sprite size range
    MIN_SIZE: 200,
    MAX_SIZE: 2000,

    // Procedural texture resolution (power of 2)
    TEXTURE_SIZE: 64,

    // Default opacity (can be overridden by palette)
    DEFAULT_OPACITY: 0.8,

    // Clouds fade when ambient light drops below this
    // (lowered since we reduced ambient for shadow-based lighting)
    FADE_THRESHOLD: 0.12,

    // Distance over which clouds fade in (prevents pop-in)
    // Must be less than SKY.RADIUS so clouds stay inside sky sphere
    FADE_IN_DISTANCE: 4000,
    FADE_IN_DISTANCE_MOBILE: 2200,
};

// =============================================================================
// DAY/NIGHT CYCLE SETTINGS
// =============================================================================

export const COLOR_CYCLE = {
    // Enable automatic day/night color cycling
    ENABLED: true,

    // Real-time duration of one full 24-hour game day (in seconds)
    // 19008 seconds = 5 hours 16 minutes 48 seconds
    DURATION: 19008,

    // Sub-cycle: adds variety within each time-of-day phase
    SUB_CYCLE: {
        // Duration of sub-cycle in game seconds (90 game minutes)
        DURATION: 5400,

        // Transition time between sub-cycle colors (10 game minutes)
        TRANSITION_DURATION: 600,
    },

    // Biome transition: gradual color blending when switching biomes
    BIOME_TRANSITION: {
        // Duration in game seconds for colors to fully transition to new biome
        // 1800 game seconds = 30 game minutes (feels like ~5-10 real seconds at normal speed)
        DURATION: 100,
    },

    // Time boundaries (normalized 0-1, where 0 = midnight, 0.5 = noon)
    // Used to determine which palette to use based on game time
    PHASE_BOUNDARIES: {
        DAWN_START: 0.208,    // 5:00 AM
        DAWN_END: 0.292,      // 7:00 AM
        DAY_END: 0.667,       // 4:00 PM
        SUNSET_END: 0.792,    // 7:00 PM
        // Night: 7:00 PM - 5:00 AM
    },

    // Palette indices for each phase
    PALETTE_INDEX: {
        DAY: 0,
        SUNSET: 1,
        NIGHT: 2,
        DAWN: 3,
    },
};

// =============================================================================
// CAR SETTINGS
// =============================================================================

export const CAR = {
    // Base position relative to camera (car-local space)
    // X: left/right, Y: up/down, Z: forward/back
    BASE_POSITION: { x: 0.4, y: -1.3, z: 0.2 },

    // Rotation smoothing for lag effect (0-1, lower = more lag)
    ROTATION_LERP: 0.1,

    // Jostle animation settings
    JOSTLE: {
        // Speed multiplier for jostle frequency
        FREQUENCY_MULTIPLIER: 0.5,

        // Maximum jostle amplitude at high speed
        MAX_AMPLITUDE: 0.02,

        // Speed threshold where jostle reaches max
        SPEED_THRESHOLD: 100,
    },
};

// =============================================================================
// EQUIPMENT SLOT POSITIONS
// =============================================================================

export const EQUIPMENT = {
    // All positions are relative to car (car-local space)
    // X: left/right, Y: up/down, Z: forward/back
    // Note: Car is rotated 180°, so positive Z = toward camera

    SLOTS: {
        DASHBOARD: { x: 0.17, y: 1.15, z: 0.6 },
        KEYCHAIN: { x: 0.3, y: 1.1, z: 0.5 },
        REARVIEW: { x: 0.05, y: 1.3, z: 0.5 },
    },

    // Scale multiplier for equipped item primitives
    // 1.0 = normal, 0.5 = half size, 2.0 = double
    ITEM_SCALE: 0.1,
};

// =============================================================================
// BIRD SETTINGS
// =============================================================================

export const BIRDS = {
    ENABLED: true,

    // Number of birds in the flock
    COUNT: 5,

    // Bird sprite/mesh size
    SIZE: 22,

    // Height range above ground
    MIN_HEIGHT: 500,
    MAX_HEIGHT: 2000,

    // Flight speed range
    MIN_SPEED: 60,
    MAX_SPEED: 300,

    // Birds fade when ambient light drops below this
    // (lowered since we reduced ambient for shadow-based lighting)
    FADE_THRESHOLD: 0.12,
};

// =============================================================================
// PLANE SETTINGS
// =============================================================================

export const PLANES = {
    ENABLED: true,

    // Plane mesh size
    SIZE: 15,

    // Height range
    MIN_HEIGHT: 600,
    MAX_HEIGHT: 3000,

    // Spawn distance range
    MIN_SPAWN_DISTANCE: 3000,
    MAX_SPAWN_DISTANCE: 4000,

    // Flight speed
    SPEED: 200,

    // Spawn timing (seconds between spawns)
    MIN_SPAWN_INTERVAL: 30,
    MAX_SPAWN_INTERVAL: 60,

    // Maximum planes in scene at once
    MAX_COUNT: 2,
};

// =============================================================================
// TIMING CONSTANTS
// =============================================================================

export const TIMING = {
    // Target frame rate for fallback timer (when tab is backgrounded)
    FALLBACK_FPS: 60,

    // Delay before enabling object spawning after game start
    SPAWN_ENABLE_DELAY: 500,

    // Delay before starting fade-in after game start
    FADE_START_DELAY: 1300,
};

// =============================================================================
// WORLD/CHUNK SETTINGS
// =============================================================================

export const WORLD = {
    // Number of road chunks to create (covers fog distance)
    CHUNK_COUNT: 100,
    CHUNK_COUNT_MOBILE: 40,
};
